/* eslint-disable @typescript-eslint/no-explicit-any */
import isNil from 'lodash/isNil'
import {mmap} from '@freckle/maybe'
import {
  isStaging,
  isTestEnv,
  isLocalhost,
  isProductionEnv
} from '@freckle/educator-entities/ts/common/helpers/common-api-helper'
import {Environments} from '@freckle/educator-entities/ts/common/helpers/environment'
import {setupRenaissanceKeepaliveIfNecessary} from '@freckle/educator-entities/ts/common/helpers/renaissance-keepalive'
import {
  isAnyPremium,
  isMathPremium,
  isElaPremium
} from '@freckle/educator-entities/ts/common/logic/premium-access'
import type {PremiumLicensesAttrs} from '@freckle/educator-entities/ts/common/models/premium-licenses'
import {
  isManagedByClassLink,
  isManagedByClever,
  isManagedByRenaissance
} from '@freckle/educator-entities/ts/roster/identity-provider'
import type {DistrictAttrs} from '@freckle/educator-entities/ts/roster/models/district'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {schoolHasStarIntegration} from '@freckle/educator-entities/ts/roster/models/school'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import {displayNotification} from '@freckle/classroom/ts/common/helpers/helpers'
import type {TeacherAttrs} from '@freckle/classroom/ts/users/models/teacher'

function getStudentGrades(students: Array<StudentAttrs>): Set<number> {
  const grades = new Set(students.map(student => student.grade))
  return grades
}

export function initializePendo({
  teacher,
  districts,
  schools,
  premiumLicenses,
  students,
  hasStarReadingPlacements,
  hasStarMathPlacements
}: {
  teacher: TeacherAttrs
  districts: Array<DistrictAttrs>
  schools: Array<SchoolAttrs>
  premiumLicenses: PremiumLicensesAttrs
  students: Array<StudentAttrs>
  hasStarReadingPlacements: boolean
  hasStarMathPlacements: boolean
}) {
  if (isStaging() || isTestEnv() || isLocalhost()) {
    return
  }
  const district = districts[0] ?? null
  const school = schools[0] ?? null
  const environment = teacher.environment
  const tagEnvironment = (value: string | number): string => {
    const env = isTestEnv() ? 'test-' : isProductionEnv() ? '' : 'dev-'
    return `${env}${value}`
  }

  type PendoVisitorIdT = {
    id: string | number
    type: 'global' | 'rgp' | 'local'
  }

  const getPrimaryVisitorIdData = (teacher: TeacherAttrs): PendoVisitorIdT => {
    if (teacher.renaissanceRPIdentifier !== null && teacher.renaissanceRPIdentifier !== undefined) {
      return {
        id: teacher.renaissanceRPIdentifier,
        type: 'rgp'
      }
    } else if (teacher.gurId !== null && teacher.gurId !== '') {
      return {
        id: teacher.gurId,
        type: 'global'
      }
    } else {
      return {
        id: `${teacher.id}-teacher`, //appending -teacher to differential teacher and student ids
        type: 'local'
      }
    }
  }

  // Renaissance Pendo instance initialization
  ;(window as any).pendo.initialize({
    visitor: {
      // standard metadata
      id: tagEnvironment(getPrimaryVisitorIdData(teacher).id),
      lang_fr: teacher.language,
      role_fr: teacher.role,
      local_user_id_fr: teacher.id,

      // basic data for Renaissance Pendo
      visitor_id_type: tagEnvironment(getPrimaryVisitorIdData(teacher).type),
      global_user_id: teacher.gurId,
      rgp_user_id: teacher.renaissanceRPIdentifier,
      user_type: teacher.role === 'parent' ? 'parent' : 'teacher', //user_type should be values for OneRoster role, outlined here: https://www.imsglobal.org/oneroster-v11-final-specification#_Toc480452025
      crm_account_number: school?.crmAccountNumber,

      // custom metadata
      app_fr: 'classroom',
      focus_mode_fr: teacher.focusMode,
      leaderboard_fr: teacher.leaderboard,
      environment_fr: !isNil(environment) ? Environments.forDisplay(environment) : null,
      has_class_link_id_fr: !isNil(teacher.classLinkTeacherId),
      has_clever_id_fr: !isNil(teacher.cleverTeacherId),
      is_managed_by_clever_fr: isManagedByClever(teacher),
      is_managed_by_classlink_fr: isManagedByClassLink(teacher),
      is_managed_by_renaissance_fr: isManagedByRenaissance(teacher),
      managed_by_idp_fr: isManagedByRenaissance(teacher)
        ? 'renaissance'
        : isManagedByClever(teacher)
          ? 'clever'
          : isManagedByClassLink(teacher)
            ? 'class_link'
            : '',
      course_subjects_fr: mmap(x => x.join(','), teacher.courseSubjects),
      onboarding_grades_fr: mmap(x => x.join(','), teacher.onboardingGrades),
      student_grades_fr: mmap(x => x.join(','), Array.from(getStudentGrades(students))),
      created_at_fr: teacher.createdAt.toISOString(),
      renaissance_id_fr: teacher.renaissanceRPIdentifier,
      //teacher has students with star reading placements
      has_students_with_star_reading_placements_fr: hasStarReadingPlacements,
      //teacher has students with star math placements
      has_students_with_star_math_placements_fr: hasStarMathPlacements,
      //whether teacher's school has star integration enabled
      has_school_with_star_enabled_fr: school ? schoolHasStarIntegration(school) : false,
      has_premium_math_fr: isMathPremium(premiumLicenses),
      has_premium_ela_fr: isElaPremium(premiumLicenses),
      // Timestamp at which the teacher FIRST purchased classroom licenses.
      classroom_license_purchased_at_fr: teacher.classroomLicensePurchasedAt?.toISOString(),
      // Timestamps at which licenses expire
      math_premium_end_date_fr: premiumLicenses.mathPremiumUntil?.toISOString() ?? null,
      ela_premium_end_date_fr: premiumLicenses.elaPremiumUntil?.toISOString() ?? null,
      social_studies_premium_end_date_fr:
        premiumLicenses.socialStudiesPremiumUntil?.toISOString() ?? null,
      science_premium_end_date_fr: premiumLicenses.sciencePremiumUntil?.toISOString() ?? null
    },
    account: {
      // standard metadata
      id: school && school.renaissanceSchoolClient ? school.renaissanceSchoolClient.clientId : null,
      name: school ? school.name : null,
      is_paying_fr: isAnyPremium(premiumLicenses),

      // custom metadata
      school_id_fr: school ? school.id : null,
      school_active_idp_fr: school?.activeIdp,
      district_id_fr: district ? district.id : null,
      district_name_fr: district ? district.name : null,
      administrative_area_fr: district ? district.administrativeArea : null,
      has_class_link_id_fr: !isNil(district?.classLinkDistrictId),
      has_clever_id_fr: !isNil(district?.cleverDistrictId)
    }
  })
}

// check that Pendo is loaded before calling it
function safePendoTrack(name: string, payload: object) {
  if ((window as any).pendo?.track !== undefined) {
    ;(window as any).pendo.track(name, payload)
  } else {
    console.warn('Freckle Pendo instance is not loaded.')
  }
}

type StudentPreTPAssignmentTracking = {
  key: 'preTPStudentRecommendationTracking'
  eventData: object
}
export function pendoTrackPreTPStudentAssignment({key, eventData}: StudentPreTPAssignmentTracking) {
  safePendoTrack(key, eventData)
}

type LegacyRedirectTracking = {
  key: 'LegacyRedirect'
  eventData: object
}
export function pendoTrackLegacyRedirect({key, eventData}: LegacyRedirectTracking) {
  safePendoTrack(key, eventData)
}

export function showErrorHandlerAndFail(routeStr: string, msg: string) {
  console.error(`${routeStr}: ${msg}`)
  displayNotification('error', msg)
  return
}

export function setupRenaissanceKeepalive(teacher: TeacherAttrs) {
  //setupRenaissanceKeepaliveIfNecessary is IIFE based.
  //Running multiple times will not create multiple intervals.
  setupRenaissanceKeepaliveIfNecessary(teacher.renaissanceKeepalive)
}
